import React, { useState, useEffect, useMemo, useCallback, createContext, useContext } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { ShoppingBag, Search, Menu, X, Star, ChevronRight, ChevronLeft, Truck, Leaf, Tags, Headphones, MessageCircle, Plus, Minus, Trash2, User, LogOut, Lock, CreditCard, Check, AlertCircle, Loader2, Heart, Filter, Package, Mail, Phone, Edit2, KeyRound, ArrowLeft, SlidersHorizontal, Share2, Facebook, Twitter, Copy, Clock } from 'lucide-react';
import AdminDashboard from './AdminDashboard';
import {
  AboutPage, ContactPage, DeliveryPage, FAQsPage, PrivacyPage, TermsPage, ReturnsPage, CategoryPage,
} from './StaticPages';

// ============================================
// AUTH CONTEXT
// ============================================
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ============================================
// AUTH PROVIDER
// ============================================
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(() => localStorage.getItem('tisho_token'));

  useEffect(() => {
    const initAuth = async () => {
      const savedToken = localStorage.getItem('tisho_token');
      if (savedToken) {
        try {
          const res = await fetch(`${API_URL}/api/auth/me`, {
            headers: { 'Authorization': `Bearer ${savedToken}` }
          });
          if (res.ok) {
            const userData = await res.json();
            setUser(userData);
            setToken(savedToken);
          } else {
            localStorage.removeItem('tisho_token');
            setToken(null);
          }
        } catch (e) {
          localStorage.removeItem('tisho_token');
          setToken(null);
        }
      }
      setIsLoading(false);
    };
    initAuth();
  }, []);

  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Login failed');
    }
    
    // Check if verification is required
    if (data.requires_verification) {
      return { requiresVerification: true, email: data.email, message: data.message };
    }
    
    localStorage.setItem('tisho_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const register = async (email, password, fullName) => {
    const res = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, full_name: fullName })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Registration failed');
    }
    
    // Registration now requires email verification
    return { requiresVerification: true, email: data.email, message: data.message };
  };

  const verifyEmail = async (email, code) => {
    const res = await fetch(`${API_URL}/api/auth/verify-email`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Verification failed');
    }
    
    localStorage.setItem('tisho_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  const resendCode = async (email) => {
    const res = await fetch(`${API_URL}/api/auth/resend-verification-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to resend code');
    }
    
    return data;
  };

  // P1 - Forgot Password
  const forgotPassword = async (email) => {
    const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to send reset code');
    }
    
    return data;
  };

  // P1 - Reset Password
  const resetPassword = async (email, code, newPassword) => {
    const res = await fetch(`${API_URL}/api/auth/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, code, new_password: newPassword })
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to reset password');
    }
    
    return data;
  };

  // P1 - Update Profile
  const updateProfile = async (profileData) => {
    const res = await fetch(`${API_URL}/api/auth/profile`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify(profileData)
    });
    
    const data = await res.json();
    
    if (!res.ok) {
      throw new Error(data.detail || 'Failed to update profile');
    }
    
    setUser(data);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('tisho_token');
    setToken(null);
    setUser(null);
  };

  // ============================================
  // GOOGLE SIGN-IN — Firebase popup (with redirect fallback)
  // The Firebase SDK is loaded lazily so a Firebase outage / blocked CDN
  // never breaks the regular email login flow.
  // ============================================
  const loginWithGoogle = async () => {
    const { signInWithGoogle } = await import('./firebase');
    const idToken = await signInWithGoogle();
    if (!idToken) {
      // Redirect path — page is navigating away; finishGoogleRedirect handles
      // the result when the user returns.
      return { redirected: true };
    }
    const res = await fetch(`${API_URL}/api/auth/firebase-login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id_token: idToken }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Google sign-in failed');
    localStorage.setItem('tisho_token', data.access_token);
    setToken(data.access_token);
    setUser(data.user);
    return data;
  };

  // After signInWithRedirect, this picks up the result on app boot.
  const finishGoogleRedirect = async () => {
    try {
      const { consumeRedirectResult } = await import('./firebase');
      const idToken = await consumeRedirectResult();
      if (!idToken) return null;
      const res = await fetch(`${API_URL}/api/auth/firebase-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id_token: idToken }),
      });
      const data = await res.json();
      if (!res.ok) return null;
      localStorage.setItem('tisho_token', data.access_token);
      setToken(data.access_token);
      setUser(data.user);
      return data;
    } catch (_) {
      return null;
    }
  };

  // Listen once on mount for a returning redirect-sign-in
  useEffect(() => {
    finishGoogleRedirect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider value={{ 
      user, token, isLoading, login, register, verifyEmail, resendCode, 
      forgotPassword, resetPassword, updateProfile, logout, loginWithGoogle,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================
// MAIN APP COMPONENT
// ============================================
function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppContent() {
  const { user, token, isLoading: authLoading, logout, isAuthenticated } = useAuth();
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('tisho_cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [wishlist, setWishlist] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [notification, setNotification] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutSuccess, setCheckoutSuccess] = useState(null);
  const [pendingCartAction, setPendingCartAction] = useState(null);
  
  // P1 & P2 - New page states
  const [currentView, setCurrentView] = useState('home'); // home, profile, wishlist, orders
  const [filters, setFilters] = useState({
    category: '',
    minPrice: '',
    maxPrice: '',
    minRating: '',
    sortBy: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  const productsPerPage = 24;

  // Load products
  useEffect(() => {
    fetch(`${API_URL}/api/products`)
      .then(res => res.json())
      .then(data => setProducts(data))
      .catch(err => console.error('Error loading products:', err));
  }, []);

  // Load wishlist when authenticated
  useEffect(() => {
    if (isAuthenticated && token) {
      fetch(`${API_URL}/api/wishlist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(res => res.ok ? res.json() : { items: [] })
        .then(data => setWishlist(data.items || []))
        .catch(() => setWishlist([]));
    } else {
      setWishlist([]);
    }
  }, [isAuthenticated, token]);

  // Save cart to localStorage
  useEffect(() => {
    localStorage.setItem('tisho_cart', JSON.stringify(cart));
  }, [cart]);

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check for checkout return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId && token) {
      pollPaymentStatus(sessionId);
    }
  }, [token]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      showNotification('Payment status check timed out. Please check your orders.', 'error');
      return;
    }

    try {
      const res = await fetch(`${API_URL}/api/checkout/status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        const data = await res.json();
        
        if (data.payment_status === 'paid') {
          setCheckoutSuccess(data);
          setCart([]); // Clear cart
          localStorage.removeItem('tisho_cart');
          // Clean URL
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
      }
      
      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  // Filter products by search
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const query = searchQuery.toLowerCase();
    let filtered = products.filter(p => 
      p.name?.toLowerCase().includes(query) ||
      p.description?.toLowerCase().includes(query)
    );
    
    // Apply filters
    if (filters.category) {
      filtered = filtered.filter(p => p.category?.toLowerCase().includes(filters.category.toLowerCase()));
    }
    if (filters.minPrice) {
      filtered = filtered.filter(p => p.price >= parseFloat(filters.minPrice));
    }
    if (filters.maxPrice) {
      filtered = filtered.filter(p => p.price <= parseFloat(filters.maxPrice));
    }
    if (filters.minRating) {
      filtered = filtered.filter(p => (p.star || 0) >= parseFloat(filters.minRating));
    }
    
    // Apply sorting
    if (filters.sortBy === 'price_asc') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (filters.sortBy === 'price_desc') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (filters.sortBy === 'rating') {
      filtered.sort((a, b) => (b.star || 0) - (a.star || 0));
    }
    
    return filtered;
  }, [products, searchQuery, filters]);

  // Paginated products
  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * productsPerPage;
    return filteredProducts.slice(start, start + productsPerPage);
  }, [filteredProducts, currentPage]);

  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Cart functions - now require auth
  const addToCart = useCallback((product) => {
    if (!isAuthenticated) {
      setPendingCartAction({ type: 'add', product });
      setShowAuthModal(true);
      showNotification('Please sign in to add items to cart', 'info');
      return;
    }
    
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id);
      if (existing) {
        return prev.map(item => 
          item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
    setIsCartOpen(true);
    showNotification(`${product.name} added to cart!`);
  }, [isAuthenticated]);

  // P2 - Wishlist functions
  const toggleWishlist = useCallback(async (product) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      showNotification('Please sign in to save items', 'info');
      return;
    }
    
    const isInWishlist = wishlist.some(item => item.product_id === String(product.id));
    
    try {
      if (isInWishlist) {
        await fetch(`${API_URL}/api/wishlist/remove/${product.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setWishlist(prev => prev.filter(item => item.product_id !== String(product.id)));
        showNotification('Removed from wishlist');
      } else {
        await fetch(`${API_URL}/api/wishlist/add`, {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}` 
          },
          body: JSON.stringify({
            product_id: String(product.id),
            name: product.name,
            price: product.price,
            image: product.image
          })
        });
        setWishlist(prev => [...prev, { 
          product_id: String(product.id), 
          name: product.name, 
          price: product.price, 
          image: product.image 
        }]);
        showNotification('Added to wishlist!');
      }
    } catch (err) {
      showNotification('Failed to update wishlist', 'error');
    }
  }, [isAuthenticated, wishlist, token]);

  const isInWishlist = useCallback((productId) => {
    return wishlist.some(item => item.product_id === String(productId));
  }, [wishlist]);

  // Handle pending action after login
  useEffect(() => {
    if (isAuthenticated && pendingCartAction) {
      if (pendingCartAction.type === 'add') {
        setCart(prev => {
          const existing = prev.find(item => item.id === pendingCartAction.product.id);
          if (existing) {
            return prev.map(item => 
              item.id === pendingCartAction.product.id ? { ...item, quantity: item.quantity + 1 } : item
            );
          }
          return [...prev, { ...pendingCartAction.product, quantity: 1 }];
        });
        setIsCartOpen(true);
        showNotification(`${pendingCartAction.product.name} added to cart!`);
      }
      setPendingCartAction(null);
    }
  }, [isAuthenticated, pendingCartAction]);

  const updateQuantity = useCallback((productId, delta) => {
    setCart(prev => {
      const item = prev.find(i => i.id === productId);
      if (!item) return prev;
      if (item.quantity + delta <= 0) {
        return prev.filter(i => i.id !== productId);
      }
      return prev.map(i => 
        i.id === productId ? { ...i, quantity: i.quantity + delta } : i
      );
    });
  }, []);

  const removeFromCart = useCallback((productId) => {
    setCart(prev => prev.filter(i => i.id !== productId));
  }, []);

  const cartTotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  }, [cart]);

  const cartCount = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  }, [cart]);

  const wishlistCount = wishlist.length;

  // Proceed to checkout
  const proceedToCheckout = () => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      showNotification('Please sign in to checkout', 'info');
      return;
    }
    
    if (cart.length === 0) {
      showNotification('Your cart is empty', 'error');
      return;
    }
    
    setIsCartOpen(false);
    setShowCheckout(true);
  };

  // Notification
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" />
      </div>
    );
  }

  // Show checkout success page
  if (checkoutSuccess) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
<Header isScrolled={false} cartCount={0} searchQuery="" setSearchQuery={() => {}} setIsCartOpen={() => {}} setIsMobileMenuOpen={() => {}} user={user} onLogout={logout} onShowAuth={() => setShowAuthModal(true)} />
        <CheckoutSuccessPage order={checkoutSuccess.order} onContinue={() => { setCheckoutSuccess(null); window.location.href = '/'; }} />
      </div>
    );
  }

  // Show checkout page
  if (showCheckout) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
<Header isScrolled={false} cartCount={cartCount} wishlistCount={wishlistCount} searchQuery="" setSearchQuery={() => {}} setIsCartOpen={() => {}} setIsMobileMenuOpen={() => {}} user={user} onLogout={logout} onShowAuth={() => setShowAuthModal(true)} onShowProfile={() => setCurrentView('profile')} onShowWishlist={() => setCurrentView('wishlist')} />
        <CheckoutPage cart={cart} cartTotal={cartTotal} products={products} user={user} token={token} onBack={() => setShowCheckout(false)} showNotification={showNotification} />
      </div>
    );
  }

  // P1 - Profile Page
  if (currentView === 'profile' && isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
<Header isScrolled={isScrolled} cartCount={cartCount} wishlistCount={wishlistCount} searchQuery="" setSearchQuery={() => {}} setIsCartOpen={setIsCartOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} user={user} onLogout={logout} onShowAuth={() => setShowAuthModal(true)} onShowProfile={() => setCurrentView('profile')} onShowWishlist={() => setCurrentView('wishlist')} />
        <ProfilePage user={user} token={token} onBack={() => setCurrentView('home')} showNotification={showNotification} onLogout={logout} />
        <Footer />
      </div>
    );
  }

  // P2 - Wishlist Page
  if (currentView === 'wishlist' && isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
<Header isScrolled={isScrolled} cartCount={cartCount} wishlistCount={wishlistCount} searchQuery="" setSearchQuery={() => {}} setIsCartOpen={setIsCartOpen} setIsMobileMenuOpen={setIsMobileMenuOpen} user={user} onLogout={logout} onShowAuth={() => setShowAuthModal(true)} onShowProfile={() => setCurrentView('profile')} onShowWishlist={() => setCurrentView('wishlist')} />
        <WishlistPage wishlist={wishlist} onBack={() => setCurrentView('home')} addToCart={addToCart} toggleWishlist={toggleWishlist} />
        <Footer />
      </div>
    );
  }

  // Home page content
  const homeContent = (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
<Header 
        isScrolled={isScrolled}
        cartCount={cartCount}
        wishlistCount={wishlistCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setIsCartOpen={setIsCartOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        user={user}
        onLogout={logout}
        onShowAuth={() => setShowAuthModal(true)}
        onShowProfile={() => setCurrentView('profile')}
        onShowWishlist={() => setCurrentView('wishlist')}
      />

      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onShowAuth={() => setShowAuthModal(true)}
        onShowProfile={() => setCurrentView('profile')}
        onShowWishlist={() => setCurrentView('wishlist')}
      />
      <HeroSection />
      <CategoriesSection />
      <FeaturesSection />
      
      {/* P2 - Product Filters */}
      <ProductFilters 
        filters={filters} 
        setFilters={setFilters} 
        showFilters={showFilters} 
        setShowFilters={setShowFilters}
        totalProducts={filteredProducts.length}
      />
      
      <ProductsSection 
        products={paginatedProducts} 
        addToCart={addToCart} 
        currentPage={currentPage} 
        totalPages={totalPages} 
        setCurrentPage={setCurrentPage}
        toggleWishlist={toggleWishlist}
        isInWishlist={isInWishlist}
      />
      <PromoSection showNotification={showNotification} />
      
      <CartSidebar 
        isOpen={isCartOpen}
        cart={cart}
        cartTotal={cartTotal}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        onClose={() => setIsCartOpen(false)}
        onCheckout={proceedToCheckout}
        isAuthenticated={isAuthenticated}
        onShowAuth={() => setShowAuthModal(true)}
      />

      <Footer />
      <FloatingButtons setIsChatOpen={setIsChatOpen} />
      <ChatWidget isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      
      {/* Auth Modal */}
      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} showNotification={showNotification} />
      )}

      {/* Notification */}
      {notification && (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full shadow-xl z-[9999] flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500' : notification.type === 'info' ? 'bg-blue-500' : 'bg-[#064E3B]'
        } text-white`}>
          {notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
          {notification.message}
        </div>
      )}
    </div>
  );

  // Product detail page content
  const productDetailContent = (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
<Header 
        isScrolled={isScrolled}
        cartCount={cartCount}
        wishlistCount={wishlistCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        setIsCartOpen={setIsCartOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        user={user}
        onLogout={logout}
        onShowAuth={() => setShowAuthModal(true)}
        onShowProfile={() => setCurrentView('profile')}
        onShowWishlist={() => setCurrentView('wishlist')}
      />
      
      <ProductDetailPage 
        products={products}
        addToCart={addToCart}
        toggleWishlist={toggleWishlist}
        isInWishlist={isInWishlist}
        showNotification={showNotification}
      />
      
      <CartSidebar 
        isOpen={isCartOpen}
        cart={cart}
        cartTotal={cartTotal}
        updateQuantity={updateQuantity}
        removeFromCart={removeFromCart}
        onClose={() => setIsCartOpen(false)}
        onCheckout={proceedToCheckout}
        isAuthenticated={isAuthenticated}
        onShowAuth={() => setShowAuthModal(true)}
      />
      
      <Footer />
      <FloatingButtons setIsChatOpen={setIsChatOpen} />
      <ChatWidget isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} />
      
      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} showNotification={showNotification} />
      )}
      
      {notification && (
        <div className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-6 py-3 rounded-full shadow-xl z-[9999] flex items-center gap-2 ${
          notification.type === 'error' ? 'bg-red-500' : notification.type === 'info' ? 'bg-blue-500' : 'bg-[#064E3B]'
        } text-white`}>
          {notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
          {notification.message}
        </div>
      )}
    </div>
  );

  // Shell wrapper for static / category pages so they share the same Header + Footer + Cart
  const shellFor = (inner) => (
    <div className="min-h-screen bg-[#FDFBF7]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
<Header
        isScrolled={isScrolled}
        cartCount={cartCount}
        wishlistCount={wishlistCount}
        searchQuery=""
        setSearchQuery={() => {}}
        setIsCartOpen={setIsCartOpen}
        setIsMobileMenuOpen={setIsMobileMenuOpen}
        user={user}
        onLogout={logout}
        onShowAuth={() => setShowAuthModal(true)}
        onShowProfile={() => setCurrentView('profile')}
        onShowWishlist={() => setCurrentView('wishlist')}
      />
      <MobileMenu
        isOpen={isMobileMenuOpen}
        onClose={() => setIsMobileMenuOpen(false)}
        onShowAuth={() => setShowAuthModal(true)}
        onShowProfile={() => setCurrentView('profile')}
        onShowWishlist={() => setCurrentView('wishlist')}
      />
      {inner}
      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        cartTotal={cartTotal}
        removeFromCart={removeFromCart}
        updateQuantity={updateQuantity}
        onCheckout={() => { setIsCartOpen(false); setCurrentView('checkout'); }}
        isAuthenticated={isAuthenticated}
        onShowAuth={() => setShowAuthModal(true)}
      />
      <Footer />
      {showAuthModal && (
        <AuthModal onClose={() => setShowAuthModal(false)} showNotification={showNotification} onSuccess={() => { setShowAuthModal(false); showNotification('Signed in successfully'); }} />
      )}
    </div>
  );

  const categoryProps = { addToCart, toggleWishlist, wishlist, showNotification, initialProducts: products };

  return (
    <Routes>
      <Route path="/" element={homeContent} />
      <Route path="/product/:id" element={productDetailContent} />
      <Route path="/admin" element={<AdminDashboard />} />
      <Route path="/about" element={shellFor(<AboutPage />)} />
      <Route path="/contact" element={shellFor(<ContactPage />)} />
      <Route path="/delivery" element={shellFor(<DeliveryPage />)} />
      <Route path="/faqs" element={shellFor(<FAQsPage />)} />
      <Route path="/privacy" element={shellFor(<PrivacyPage />)} />
      <Route path="/terms" element={shellFor(<TermsPage />)} />
      <Route path="/returns" element={shellFor(<ReturnsPage />)} />
      <Route path="/shop" element={shellFor(<CategoryPage title="All groceries" {...categoryProps} />)} />
      <Route path="/category/:name" element={shellFor(<CategoryRouteWrapper {...categoryProps} />)} />
      <Route path="*" element={homeContent} />
    </Routes>
  );
}

// Small wrapper so /category/:name feeds the URL param into CategoryPage
function CategoryRouteWrapper(props) {
  const { name } = useParams();
  const pretty = decodeURIComponent(name || '').replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  return <CategoryPage category={pretty} title={pretty} {...props} />;
}

// ============================================
// AUTH MODAL
// ============================================
function AuthModal({ onClose, showNotification = () => {} }) {
  const { login, register, verifyEmail, resendCode, forgotPassword, resetPassword } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Verification state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  // P1 - Forgot Password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const result = await login(email, password);
        if (result.requiresVerification) {
          setVerificationEmail(result.email);
          setShowVerification(true);
          showNotification(result.message, 'info');
        } else {
          showNotification('Welcome back!');
          onClose();
        }
      } else {
        if (!fullName.trim()) {
          setError('Full name is required');
          setLoading(false);
          return;
        }
        const result = await register(email, password, fullName);
        if (result.requiresVerification) {
          setVerificationEmail(result.email);
          setShowVerification(true);
          showNotification(result.message, 'info');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const { loginWithGoogle } = useAuth();
  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await loginWithGoogle();
      if (result?.redirected) {
        // Browser is navigating to Google — leave the loading spinner running
        return;
      }
      showNotification('Welcome!');
      onClose();
    } catch (err) {
      // Friendly mapping for the most common Firebase auth errors
      const code = err?.code || '';
      const friendly =
        code === 'auth/popup-closed-by-user' ? 'Sign-in cancelled.' :
        code === 'auth/network-request-failed' ? "Network error — check your connection and try again." :
        code === 'auth/unauthorized-domain' ? "This site isn't authorized for Google sign-in yet. Please contact support." :
        code === 'auth/account-exists-with-different-credential' ? "An account with this email already exists. Please sign in with email instead." :
        err?.message || 'Google sign-in failed';
      setError(friendly);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyEmail(verificationEmail, verificationCode);
      showNotification('Email verified successfully! Welcome to TishoFood!');
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0) return;
    
    setResendLoading(true);
    setError('');

    try {
      await resendCode(verificationEmail);
      showNotification('Verification code sent! Check your email.', 'info');
      setResendCooldown(60);
    } catch (err) {
      setError(err.message);
    } finally {
      setResendLoading(false);
    }
  };

  // P1 - Forgot Password Handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await forgotPassword(resetEmail);
      showNotification('Reset code sent to your email!', 'info');
      setShowForgotPassword(false);
      setShowResetPassword(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // P1 - Reset Password Handler
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);

    try {
      await resetPassword(resetEmail, resetCode, newPassword);
      showNotification('Password reset successfully! Please login.');
      setShowResetPassword(false);
      setResetEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // P1 - Forgot Password Screen
  if (showForgotPassword) {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-[2000]" onClick={onClose} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-3xl shadow-2xl z-[2001] overflow-hidden" data-testid="forgot-password-modal">
          <div className="p-8" style={{ background: 'linear-gradient(135deg, #C2410C 0%, #9A3412 100%)' }}>
            <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white/30">
              <X className="w-4 h-4" />
            </button>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
              <KeyRound className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Forgot Password?
            </h2>
            <p className="text-white/80 mt-2">
              Enter your email and we'll send you a reset code
            </p>
          </div>

          <form onSubmit={handleForgotPassword} className="p-8 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/10"
                placeholder="you@example.com"
                data-testid="forgot-email-input"
                required
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-[#C2410C] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#9A3412] transition-all disabled:opacity-50"
              data-testid="forgot-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Mail className="w-5 h-5" />}
              {loading ? 'Sending...' : 'Send Reset Code'}
            </button>

            <button
              type="button"
              onClick={() => { setShowForgotPassword(false); setError(''); }}
              className="w-full text-center text-gray-500 hover:text-gray-700 text-sm"
            >
              ← Back to login
            </button>
          </form>
        </div>
      </>
    );
  }

  // P1 - Reset Password Screen
  if (showResetPassword) {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-[2000]" onClick={onClose} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-3xl shadow-2xl z-[2001] overflow-hidden" data-testid="reset-password-modal">
          <div className="p-8" style={{ background: 'linear-gradient(135deg, #C2410C 0%, #9A3412 100%)' }}>
            <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white/30">
              <X className="w-4 h-4" />
            </button>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Reset Password
            </h2>
            <p className="text-white/80 mt-2">
              Enter the code sent to <strong>{resetEmail}</strong>
            </p>
          </div>

          <form onSubmit={handleResetPassword} className="p-8 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reset Code</label>
              <input
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/10 text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
                maxLength={6}
                data-testid="reset-code-input"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/10"
                placeholder="••••••••"
                data-testid="reset-new-password-input"
                required
                minLength={6}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#C2410C] focus:ring-2 focus:ring-[#C2410C]/10"
                placeholder="••••••••"
                data-testid="reset-confirm-password-input"
                required
                minLength={6}
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || resetCode.length !== 6}
              className="w-full py-4 bg-[#C2410C] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#9A3412] transition-all disabled:opacity-50"
              data-testid="reset-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>

            <button
              type="button"
              onClick={() => { setShowResetPassword(false); setShowForgotPassword(true); setError(''); }}
              className="w-full text-center text-gray-500 hover:text-gray-700 text-sm"
            >
              ← Back to email entry
            </button>
          </form>
        </div>
      </>
    );
  }

  // Verification Screen
  if (showVerification) {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-[2000]" onClick={onClose} />
        <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-3xl shadow-2xl z-[2001] overflow-hidden" data-testid="verification-modal">
          <div className="p-8" style={{ background: 'linear-gradient(135deg, #064E3B 0%, #053D2E 100%)' }}>
            <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white/30" data-testid="verification-close-btn">
              <X className="w-4 h-4" />
            </button>
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mb-4">
              <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              Verify Your Email
            </h2>
            <p className="text-white/80 mt-2">
              We've sent a 6-digit code to <strong>{verificationEmail}</strong>
            </p>
          </div>

          <form onSubmit={handleVerify} className="p-8 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10 text-center text-2xl tracking-[0.5em] font-mono"
                placeholder="000000"
                maxLength={6}
                data-testid="verification-code-input"
                required
              />
              <p className="text-sm text-gray-500 mt-2 text-center">
                Enter the 6-digit code from your email
              </p>
            </div>

            {error && (
              <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl" data-testid="verification-error">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || verificationCode.length !== 6}
              className="w-full py-4 bg-[#064E3B] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#065F46] transition-all disabled:opacity-50"
              data-testid="verify-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Check className="w-5 h-5" />}
              {loading ? 'Verifying...' : 'Verify Email'}
            </button>

            <div className="text-center pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-2">Didn't receive the code?</p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendLoading || resendCooldown > 0}
                className="text-[#064E3B] font-medium hover:underline disabled:opacity-50 disabled:no-underline"
                data-testid="resend-code-btn"
              >
                {resendLoading ? 'Sending...' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
              </button>
            </div>

            <button
              type="button"
              onClick={() => { setShowVerification(false); setError(''); setVerificationCode(''); }}
              className="w-full text-center text-gray-500 hover:text-gray-700 text-sm"
              data-testid="back-to-login-btn"
            >
              ← Back to {isLogin ? 'login' : 'sign up'}
            </button>
          </form>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[2000]" onClick={onClose} />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-white rounded-3xl shadow-2xl z-[2001] overflow-hidden" data-testid="auth-modal">
        <div className="p-8" style={{ background: 'linear-gradient(135deg, #064E3B 0%, #053D2E 100%)' }}>
          <button onClick={onClose} className="absolute top-4 right-4 w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center text-white hover:bg-white/30" data-testid="auth-close-btn">
            <X className="w-4 h-4" />
          </button>
          <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
            {isLogin ? 'Welcome Back!' : 'Create Account'}
          </h2>
          <p className="text-white/80 mt-2">
            {isLogin ? 'Sign in to continue shopping' : 'Join TishoFood today'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-4">
          {!isLogin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10"
                placeholder="John Doe"
                data-testid="register-name-input"
                required={!isLogin}
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10"
              placeholder="you@example.com"
              data-testid="auth-email-input"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10"
              placeholder="••••••••"
              data-testid="auth-password-input"
              required
              minLength={6}
            />
          </div>

          {isLogin && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setShowForgotPassword(true); setError(''); }}
                className="text-sm text-[#C2410C] hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot password?
              </button>
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl" data-testid="auth-error">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-[#064E3B] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#065F46] transition-all disabled:opacity-50"
            data-testid="auth-submit-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
            {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Create Account')}
          </button>

          {/* OR divider */}
          <div className="relative my-2" aria-hidden="true">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white px-3 text-gray-400 uppercase tracking-wider">or</span>
            </div>
          </div>

          {/* Google Sign-In */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full py-3.5 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-medium flex items-center justify-center gap-3 hover:border-gray-300 hover:shadow-sm transition-all disabled:opacity-50"
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          <div className="text-center pt-4">
            <button
              type="button"
              onClick={() => { setIsLogin(!isLogin); setError(''); }}
              className="text-[#064E3B] font-medium hover:underline"
              data-testid="auth-toggle-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

// ============================================
// CHECKOUT PAGE
// ============================================
function CheckoutPage({ cart, cartTotal, products, user, token, onBack, showNotification }) {
  const [deliveryMethod, setDeliveryMethod] = useState('delivery');
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [street, setStreet] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [quote, setQuote] = useState(null);          // {shipping_cost, weight_kg, bracket, error?}
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [pickupInfo, setPickupInfo] = useState(null);

  const isPickup = deliveryMethod === 'pickup';

  // Build a { id -> product } lookup once so we can resolve fresh weight_kg
  // for every cart line — protects against stale localStorage carts that were
  // populated before products had weights.
  const productsById = useMemo(() => {
    const m = new Map();
    (products || []).forEach((p) => { if (p && p.id != null) m.set(String(p.id), p); });
    return m;
  }, [products]);

  const enrichedItems = useMemo(() => {
    return cart.map((i) => {
      const live = productsById.get(String(i.id));
      const weight_kg = i.weight_kg ?? live?.weight_kg ?? null;
      return {
        id: i.id,
        name: i.name,
        price: i.price,
        quantity: i.quantity,
        image: i.image,
        ...(weight_kg != null ? { weight_kg } : {}),
      };
    });
  }, [cart, productsById]);

  // Load pickup shop info once
  useEffect(() => {
    fetch(`${API_URL}/api/shipping/pickup-info`)
      .then((r) => r.ok ? r.json() : null)
      .then(setPickupInfo)
      .catch(() => {});
  }, []);

  // Debounced live shipping quote whenever anything relevant changes
  useEffect(() => {
    if (!cart.length) return;
    const controller = new AbortController();
    const t = setTimeout(async () => {
      setQuoteLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/shipping/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          signal: controller.signal,
          body: JSON.stringify({
            delivery_method: deliveryMethod,
            postal_code: postalCode,
            items: enrichedItems.map((i) => ({ id: i.id, quantity: i.quantity, ...(i.weight_kg != null ? { weight_kg: i.weight_kg } : {}) })),
          }),
        });
        const data = await res.json();
        setQuote(res.ok ? data : { error: data.detail || 'Unable to calculate shipping.' });
      } catch (e) {
        if (e.name !== 'AbortError') setQuote({ error: 'Unable to calculate shipping.' });
      } finally {
        setQuoteLoading(false);
      }
    }, 350);
    return () => { controller.abort(); clearTimeout(t); };
  }, [enrichedItems, postalCode, deliveryMethod, cart.length]);

  const shippingCost = quote && typeof quote.shipping_cost === 'number' ? quote.shipping_cost : null;
  const canPay = !loading && (
    isPickup
      ? Boolean(fullName.trim() && phone.trim() && shippingCost !== null)
      : Boolean(fullName.trim() && phone.trim() && houseNumber.trim() && street.trim() && city.trim() && postalCode.trim() && shippingCost !== null && !(quote && quote.error))
  );

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');
    if (!fullName.trim() || fullName.trim().length < 2) return setError('Please enter your full name');
    if (!phone.trim()) return setError('Please enter a contact phone number');
    if (!isPickup) {
      if (!houseNumber.trim()) return setError('Please enter your house / flat number');
      if (!street.trim()) return setError('Please enter your street');
      if (!city.trim()) return setError('Please enter your city');
      if (!postalCode.trim() || postalCode.trim().length < 3) return setError('Please enter a valid UK postcode');
      if (quote && quote.error) return setError(quote.error);
    }
    if (shippingCost === null) return setError('Shipping quote not ready yet. Please wait a moment.');

    setLoading(true);

    const addressLine1 = `${houseNumber.trim()} ${street.trim()}`.trim();

    try {
      const res = await fetch(`${API_URL}/api/checkout/create-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          origin_url: window.location.origin,
          full_name: fullName.trim(),
          postal_code: isPickup ? (pickupInfo?.postal_code || '') : postalCode.trim(),
          email: user.email,
          delivery_method: deliveryMethod,
          phone: phone.trim(),
          shipping_address: isPickup ? {
            full_name: fullName.trim(),
            phone: phone.trim(),
            address_line1: pickupInfo?.address_line1 || '',
            city: pickupInfo?.city || '',
            postal_code: pickupInfo?.postal_code || '',
            country: 'United Kingdom',
            notes: notes.trim(),
          } : {
            full_name: fullName.trim(),
            phone: phone.trim(),
            address_line1: addressLine1,
            address_line2: addressLine2.trim(),
            city: city.trim(),
            postal_code: postalCode.trim(),
            country: 'United Kingdom',
            notes: notes.trim(),
          },
          items: enrichedItems.map((item) => ({
            id: item.id,
            name: item.name,
            price: item.price,
            quantity: item.quantity,
            image: item.image,
            ...(item.weight_kg != null ? { weight_kg: item.weight_kg } : {}),
          })),
        }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to create checkout session');
      }
      const data = await res.json();
      window.location.href = data.checkout_url;
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';
  const total = cartTotal + (shippingCost || 0);

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="checkout-page">
      <div className="max-w-5xl mx-auto px-4 lg:px-8">
        <button
          onClick={onBack}
          className="flex items-center gap-2 text-gray-600 hover:text-[#064E3B] mb-8"
          data-testid="checkout-back-btn"
        >
          <ChevronLeft className="w-5 h-5" /> Back to Shop
        </button>

        <div className="grid lg:grid-cols-[1fr_420px] gap-8">
          {/* LEFT: Delivery method + form */}
          <div className="space-y-6">
            {/* Method toggle */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100" data-testid="delivery-method-toggle">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                How would you like to receive your order?
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setDeliveryMethod('delivery')}
                  className={`text-left p-4 rounded-2xl border-2 transition-all ${
                    !isPickup ? 'border-[#064E3B] bg-[#064E3B]/5' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="delivery-method-delivery"
                >
                  <div className="flex items-center gap-2 font-semibold text-[#064E3B]">
                    <Truck className="w-5 h-5" /> UK Delivery
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Weight-based rates. Local (DH/SR/NE) £6 flat.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setDeliveryMethod('pickup')}
                  className={`text-left p-4 rounded-2xl border-2 transition-all ${
                    isPickup ? 'border-[#064E3B] bg-[#064E3B]/5' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="delivery-method-pickup"
                >
                  <div className="flex items-center gap-2 font-semibold text-[#064E3B]">
                    <Package className="w-5 h-5" /> Shop pick-up (free)
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Collect from our South Shields shop.</p>
                </button>
              </div>
            </div>

            {/* Pickup card OR delivery address */}
            {isPickup ? (
              <div className="bg-white rounded-3xl p-6 border border-gray-100" data-testid="pickup-address-card">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#064E3B]/10 text-[#064E3B] flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{pickupInfo?.name || 'TishoFood shop'}</h3>
                    <p className="text-sm text-gray-600">
                      {pickupInfo?.address_line1}<br />
                      {pickupInfo?.city}<br />
                      {pickupInfo?.postal_code}
                    </p>
                    <p className="text-xs text-gray-500 mt-2">Hours: {pickupInfo?.hours}</p>
                    <p className="text-xs text-gray-500">Phone: {pickupInfo?.phone}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4 pt-4 border-t border-gray-100">
                  We'll ring you as soon as your order is ready to collect.
                </p>
              </div>
            ) : null}

            {/* Contact + address form */}
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {isPickup ? 'Contact details' : 'Delivery address'}
              </h2>

              <form onSubmit={handleCheckout} className="space-y-4">
                <div>
                  <label className={labelCls}>Full name <span className="text-red-500">*</span></label>
                  <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} className={inputCls} required data-testid="checkout-fullname-input" />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input type="email" value={user?.email || ''} disabled className={inputCls + ' bg-gray-50 text-gray-500'} data-testid="checkout-email-input" />
                </div>
                <div>
                  <label className={labelCls}>Phone <span className="text-red-500">*</span></label>
                  <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} className={inputCls} placeholder="07123 456789" required data-testid="checkout-phone-input" />
                </div>

                {!isPickup && (
                  <>
                    <div className="grid grid-cols-3 gap-3">
                      <div className="col-span-1">
                        <label className={labelCls}>House / flat # <span className="text-red-500">*</span></label>
                        <input type="text" value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} className={inputCls} placeholder="47" required data-testid="checkout-house-input" />
                      </div>
                      <div className="col-span-2">
                        <label className={labelCls}>Street <span className="text-red-500">*</span></label>
                        <input type="text" value={street} onChange={(e) => setStreet(e.target.value)} className={inputCls} placeholder="Westoe Road" required data-testid="checkout-street-input" />
                      </div>
                    </div>
                    <div>
                      <label className={labelCls}>Address line 2 <span className="text-gray-400 text-xs">(optional)</span></label>
                      <input type="text" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} className={inputCls} placeholder="Apartment, suite, etc." data-testid="checkout-address2-input" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className={labelCls}>City <span className="text-red-500">*</span></label>
                        <input type="text" value={city} onChange={(e) => setCity(e.target.value)} className={inputCls} placeholder="South Shields" required data-testid="checkout-city-input" />
                      </div>
                      <div>
                        <label className={labelCls}>Postcode <span className="text-red-500">*</span></label>
                        <input type="text" value={postalCode} onChange={(e) => setPostalCode(e.target.value.toUpperCase())} className={inputCls} placeholder="NE33 4LU" required data-testid="checkout-postalcode-input" />
                      </div>
                    </div>
                  </>
                )}

                <div>
                  <label className={labelCls}>Notes <span className="text-gray-400 text-xs">(optional)</span></label>
                  <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder={isPickup ? 'Anything we should know about your collection…' : 'Delivery instructions, gate code, etc.'} data-testid="checkout-notes-input" />
                </div>

                {error && (
                  <div className="flex items-center gap-2 text-red-500 text-sm bg-red-50 p-3 rounded-xl" data-testid="checkout-error">
                    <AlertCircle className="w-4 h-4" /> {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={!canPay}
                  className="w-full py-4 bg-[#064E3B] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#065F46] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="checkout-pay-btn"
                >
                  {loading ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</>
                  ) : (
                    <><CreditCard className="w-5 h-5" /> Pay £{total.toFixed(2)}</>
                  )}
                </button>
                <p className="text-xs text-gray-500 text-center">
                  <Lock className="w-3 h-3 inline mr-1" /> Secure payment powered by Stripe
                </p>
              </form>
            </div>
          </div>

          {/* RIGHT: Order summary */}
          <aside className="lg:sticky lg:top-24 h-max">
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              <h2 className="text-xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Order summary
              </h2>

              <div className="space-y-3 mb-4 max-h-64 overflow-y-auto pr-1">
                {cart.map((item) => (
                  <div key={item.id} className="flex gap-3 text-sm" data-testid={`checkout-item-${item.id}`}>
                    <img src={item.image} alt={item.name} className="w-12 h-12 object-cover rounded-lg bg-gray-100" onError={(e) => e.target.src = '/placeholder.jpg'} />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{item.name}</p>
                      <p className="text-xs text-gray-500">Qty {item.quantity}</p>
                    </div>
                    <p className="font-semibold text-[#064E3B]">£{(item.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-4 space-y-2 text-sm">
                <div className="flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span data-testid="checkout-subtotal">£{cartTotal.toFixed(2)}</span>
                </div>

                <div className="flex justify-between text-gray-600 items-start" data-testid="checkout-shipping-row">
                  <div>
                    <div>Shipping</div>
                    {quote?.bracket?.label && (
                      <div className="text-xs text-gray-400" data-testid="checkout-shipping-bracket">
                        {quote.bracket.label}{quote.weight_kg != null ? ` · ${quote.weight_kg.toFixed(2)} kg` : ''}
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {quoteLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin inline" />
                    ) : shippingCost === null ? (
                      <span className="text-xs text-gray-400" data-testid="checkout-shipping-cost">
                        {isPickup ? '£0.00' : (postalCode ? 'Calculating…' : 'Enter postcode')}
                      </span>
                    ) : (
                      <span className="font-medium" data-testid="checkout-shipping-cost">£{shippingCost.toFixed(2)}</span>
                    )}
                  </div>
                </div>

                {quote?.error && !isPickup && (
                  <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded-lg" data-testid="checkout-shipping-warning">
                    {quote.error}
                  </div>
                )}

                <div className="flex justify-between text-base font-bold pt-3 border-t border-gray-100">
                  <span>Total</span>
                  <span className="text-[#064E3B]" data-testid="checkout-total">£{total.toFixed(2)}</span>
                </div>
              </div>

              <div className="mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500 space-y-1">
                <p><strong className="text-gray-700">Shipping rates:</strong></p>
                <p>• Local zone DH / SR / NE — <strong>£6</strong> flat</p>
                <p>• 0 – 20 kg — £7.99</p>
                <p>• 20 – 30 kg — £10</p>
                <p>• 30 – 40 kg — £15</p>
                <p>• 40 – 60 kg — £25</p>
                <p>• Shop pick-up — free</p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ============================================
// CHECKOUT SUCCESS PAGE
// ============================================
function CheckoutSuccessPage({ order, onContinue }) {
  return (
    <div className="pt-24 pb-16 min-h-screen flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg mx-auto px-4 text-center">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-10 h-10 text-green-600" />
        </div>
        
        <h1 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          Order Confirmed!
        </h1>
        
        <p className="text-gray-600 mb-8">
          Thank you for your order. A confirmation email has been sent to <strong>{order?.email}</strong>.
        </p>

        <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-8 text-left">
          <h3 className="font-semibold mb-4">Order Details</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500">Order ID:</span>
              <span className="font-mono">{order?.order_id?.slice(0, 8)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Name:</span>
              <span>{order?.full_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500">Postal Code:</span>
              <span>{order?.postal_code}</span>
            </div>
            <div className="flex justify-between font-bold pt-2 border-t border-gray-100">
              <span>Total:</span>
              <span className="text-[#064E3B]">£{order?.total?.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <button
          onClick={onContinue}
          className="px-8 py-4 bg-[#064E3B] text-white rounded-full font-semibold hover:bg-[#065F46] transition-all"
          data-testid="continue-shopping-btn"
        >
          Continue Shopping
        </button>
      </div>
    </div>
  );
}

// ============================================
// HEADER COMPONENT (Updated with auth)
// ============================================
function Header({ isScrolled, cartCount, wishlistCount = 0, searchQuery, setSearchQuery, setIsCartOpen, setIsMobileMenuOpen, user, onLogout, onShowAuth, onShowProfile, onShowWishlist }) {
  const [showUserMenu, setShowUserMenu] = useState(false);

  return (
    <header 
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 h-20 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-[#FDFBF7]/95 shadow-md backdrop-blur-xl' 
          : 'bg-[#FDFBF7]/85 backdrop-blur-xl'
      } border-b border-[#064E3B]/10`}
    >
      <div className="max-w-7xl mx-auto h-full px-4 lg:px-8 flex items-center justify-between gap-4">
        <a href="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src="/Tishologo.png" alt="Tisho Food" className="w-12 h-12 rounded-xl object-contain" />
          <span className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            Tisho<span className="text-[#064E3B]">Food</span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
          <NavLink href="/" active>Home</NavLink>
          <div className="relative group">
            <button className="flex items-center gap-1 px-4 py-2.5 text-gray-600 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all text-[15px] font-medium">
              Shop <ChevronRight className="w-3 h-3 transition-transform group-hover:rotate-90" />
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
              <div className="bg-white rounded-2xl shadow-xl border border-[#064E3B]/10 p-2 min-w-[200px]">
                <DropdownItem href="/shop" icon="🛒">All Groceries</DropdownItem>
                <DropdownItem href="/category/Frozen" icon="❄️">Frozen</DropdownItem>
                <DropdownItem href="/category/Oils" icon="🫒">Oils</DropdownItem>
                <DropdownItem href="/category/Rice & Grains" icon="🌾">Rice & Grains</DropdownItem>
              </div>
            </div>
          </div>
          <NavLink href="/about">About</NavLink>
          <NavLink href="/contact">Contact</NavLink>
        </nav>

        <div className="hidden md:flex flex-1 max-w-md relative" data-testid="search-wrapper">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search for products..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full h-11 pl-11 pr-4 bg-white border border-[#064E3B]/10 rounded-full text-[15px] focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10 transition-all"
            data-testid="search-input"
          />
        </div>

        <div className="flex items-center gap-2">
          {/* Wishlist Button */}
          {user && (
            <button 
              onClick={onShowWishlist}
              className="relative w-11 h-11 border border-[#064E3B]/10 text-gray-600 rounded-xl flex items-center justify-center hover:bg-[#C2410C]/5 hover:border-[#C2410C]/20 hover:text-[#C2410C] transition-all"
              data-testid="wishlist-toggle"
            >
              <Heart className="w-5 h-5" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C2410C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {wishlistCount}
                </span>
              )}
            </button>
          )}

          {user ? (
            <div className="relative">
              <button 
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="hidden md:flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all text-sm font-semibold"
                data-testid="user-menu-btn"
              >
                <User className="w-4 h-4" />
                <span className="max-w-[100px] truncate">{user.full_name}</span>
              </button>
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold truncate">{user.full_name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    <button
                      onClick={() => { onShowProfile && onShowProfile(); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="profile-nav-btn"
                    >
                      <User className="w-4 h-4" /> My Profile
                    </button>
                    <button
                      onClick={() => { onShowWishlist && onShowWishlist(); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="wishlist-nav-btn"
                    >
                      <Heart className="w-4 h-4" /> Wishlist
                      {wishlistCount > 0 && (
                        <span className="ml-auto bg-[#C2410C] text-white text-xs px-2 py-0.5 rounded-full">{wishlistCount}</span>
                      )}
                    </button>
                    <button
                      onClick={() => { onShowProfile && onShowProfile(); setShowUserMenu(false); }}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                      data-testid="orders-nav-btn"
                    >
                      <Package className="w-4 h-4" /> Order History
                    </button>
                    <div className="border-t border-gray-100 mt-1 pt-1">
                      <button
                        onClick={() => { onLogout(); setShowUserMenu(false); }}
                        className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" /> Sign Out
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          ) : (
            <button 
              onClick={onShowAuth}
              className="hidden md:flex items-center gap-2 px-4 py-2.5 text-gray-600 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all text-sm font-semibold"
              data-testid="login-btn"
            >
              <User className="w-4 h-4" />
              <span>Login</span>
            </button>
          )}
          
          <button 
            onClick={() => setIsCartOpen(true)}
            className="relative w-11 h-11 bg-[#064E3B] text-white rounded-xl flex items-center justify-center hover:bg-[#065F46] transition-all hover:scale-105"
            data-testid="cart-toggle"
          >
            <ShoppingBag className="w-5 h-5" />
            {cartCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#C2410C] text-white text-xs font-bold rounded-full flex items-center justify-center">
                {cartCount}
              </span>
            )}
          </button>
          
          <button 
            onClick={() => setIsMobileMenuOpen(true)}
            className="lg:hidden w-11 h-11 border border-[#064E3B]/10 rounded-xl flex items-center justify-center hover:bg-gray-100 transition-all"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>
      </div>
    </header>
  );
}

function NavLink({ href, children, active }) {
  return (
    <Link 
      to={href}
      className={`px-4 py-2.5 text-[15px] font-medium rounded-xl transition-all ${
        active 
          ? 'text-[#064E3B] bg-[#064E3B]/8' 
          : 'text-gray-600 hover:text-[#064E3B] hover:bg-[#064E3B]/5'
      }`}
    >
      {children}
    </Link>
  );
}

function DropdownItem({ href, icon, children }) {
  return (
    <Link 
      to={href}
      className="flex items-center gap-3 px-4 py-3 text-[15px] font-medium text-gray-600 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all"
    >
      <span>{icon}</span>
      {children}
    </Link>
  );
}

// ============================================
// MOBILE MENU
// ============================================
function MobileMenu({ isOpen, onClose, onShowAuth, onShowProfile, onShowWishlist }) {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  if (!isOpen) return null;

  const goCategory = (path) => {
    onClose();
    navigate(path);
  };

  const openProfile = () => {
    onClose();
    navigate('/');
    if (onShowProfile) onShowProfile();
  };

  const openWishlist = () => {
    onClose();
    navigate('/');
    if (onShowWishlist) onShowWishlist();
  };

  const openAuth = () => {
    onClose();
    if (onShowAuth) onShowAuth();
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate('/');
  };

  const navItems = [
    { href: '/', label: 'Home', icon: '🏠' },
    { href: '/shop', label: 'All Groceries', icon: '🛒' },
    { href: '/category/Frozen', label: 'Frozen', icon: '❄️' },
    { href: '/category/Oils', label: 'Oils', icon: '🫒' },
    { href: '/category/Rice & Grains', label: 'Rice & Grains', icon: '🌾' },
    { href: '/about', label: 'About Us', icon: 'ℹ️' },
    { href: '/contact', label: 'Contact', icon: '📧' },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[1050]" onClick={onClose} />
      <div className="fixed top-0 right-0 w-full max-w-xs h-full bg-white z-[1100] shadow-xl overflow-y-auto" data-testid="mobile-menu">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <span className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>Menu</span>
          <button onClick={onClose} className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-[#064E3B] hover:text-white transition-all" data-testid="mobile-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Account section — always visible at the top */}
        {isAuthenticated ? (
          <div className="p-4 border-b border-gray-100" data-testid="mobile-account-section">
            <div className="flex items-center gap-3 px-4 py-3 bg-[#064E3B]/5 rounded-xl mb-3">
              <div className="w-10 h-10 rounded-full bg-[#064E3B] text-white flex items-center justify-center font-bold text-sm shrink-0">
                {(user?.full_name || user?.email || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-[#064E3B] truncate" data-testid="mobile-user-name">
                  {user?.full_name || 'Welcome'}
                </div>
                <div className="text-xs text-gray-500 truncate" data-testid="mobile-user-email">
                  {user?.email}
                </div>
              </div>
            </div>
            <button
              onClick={openProfile}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all font-medium"
              data-testid="mobile-nav-profile"
            >
              <User className="w-5 h-5" />
              My Profile
            </button>
            <button
              onClick={openProfile}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all font-medium"
              data-testid="mobile-nav-orders"
            >
              <Package className="w-5 h-5" />
              My Orders
            </button>
            <button
              onClick={openWishlist}
              className="w-full flex items-center gap-3 px-4 py-3 text-gray-700 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all font-medium"
              data-testid="mobile-nav-wishlist"
            >
              <Heart className="w-5 h-5" />
              Wishlist
            </button>
          </div>
        ) : (
          <div className="p-4 border-b border-gray-100" data-testid="mobile-auth-section">
            <button
              onClick={openAuth}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#064E3B] text-white rounded-xl font-semibold hover:bg-[#053D2E] transition-all"
              data-testid="mobile-signin-btn"
            >
              <User className="w-5 h-5" />
              Sign In / Sign Up
            </button>
            <p className="text-xs text-gray-500 text-center mt-2">Sign in to view orders, wishlist & profile</p>
          </div>
        )}

        <nav className="p-4">
          {navItems.map(item => (
            <a
              key={item.href}
              href={item.href}
              onClick={(e) => { e.preventDefault(); goCategory(item.href); }}
              className="flex items-center gap-3 px-4 py-4 text-gray-600 hover:text-[#064E3B] hover:bg-[#064E3B]/5 rounded-xl transition-all font-medium"
            >
              <span>{item.icon}</span>
              {item.label}
            </a>
          ))}
        </nav>

        {isAuthenticated && (
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={handleLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-xl transition-all font-medium"
              data-testid="mobile-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ============================================
// HERO SECTION
// ============================================
function HeroSection() {
  const heroStyle = {
    position: 'relative',
    paddingTop: '80px',
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #064E3B 0%, #053D2E 100%)',
    minHeight: '600px'
  };

  return (
    <section style={heroStyle} data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="text-center lg:text-left">
            <span 
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
              style={{ backgroundColor: 'rgba(255,255,255,0.15)', color: 'white' }}
              data-testid="hero-badge"
            >
              <Truck className="w-4 h-4" /> Free delivery over £150 in SR, DH and NE only
            </span>
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              style={{ fontFamily: "'Playfair Display', serif", color: 'white' }}
              data-testid="hero-title"
            >
              Authentic <span style={{ color: '#EAB308', fontStyle: 'italic' }}>Nigerian</span> & African Foods
            </h1>
            <p 
              className="text-lg mb-8 max-w-lg mx-auto lg:mx-0"
              style={{ color: 'rgba(255,255,255,0.85)' }}
              data-testid="hero-subtitle"
            >
              Bringing the rich flavors of Africa to your doorstep. Fresh, quality ingredients for your traditional dishes.
            </p>
            <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-12">
              <Link 
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg hover:-translate-y-1 transition-all"
                style={{ 
                  fontFamily: "'Manrope', sans-serif",
                  backgroundColor: '#EAB308',
                  color: '#0F172A',
                  boxShadow: '0 10px 30px -5px rgba(234, 179, 8, 0.4)'
                }}
                data-testid="hero-shop-btn"
              >
                <ShoppingBag className="w-5 h-5" /> Shop Now
              </Link>
              <Link 
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold transition-all hover:bg-white hover:text-[#064E3B]"
                style={{ 
                  fontFamily: "'Manrope', sans-serif",
                  border: '2px solid rgba(255,255,255,0.5)',
                  color: 'white'
                }}
                data-testid="hero-learn-btn"
              >
                Learn More <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="flex gap-8 justify-center lg:justify-start">
              <Stat number="136+" label="Products" />
              <Stat number="5K+" label="Happy Customers" />
              <Stat number="24h" label="Fast Delivery" />
            </div>
          </div>
          <div className="hidden lg:block">
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1604329760661-e71dc83f8f26?w=600&q=80" 
                alt="Authentic African Dish"
                className="w-full max-w-md rounded-3xl shadow-2xl transform rotate-3"
                data-testid="hero-image"
              />
              <div 
                className="absolute bottom-8 -left-8 px-5 py-3 rounded-full shadow-xl flex items-center gap-2 font-semibold animate-bounce"
                style={{ backgroundColor: 'white', color: '#064E3B' }}
                data-testid="floating-badge"
              >
                <Star className="w-5 h-5" style={{ color: '#EAB308', fill: '#EAB308' }} />
                Premium Quality
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
        <svg viewBox="0 0 1440 120" fill="none" className="w-full">
          <path d="M0 120L60 110C120 100 240 80 360 70C480 60 600 60 720 65C840 70 960 80 1080 85C1200 90 1320 90 1380 90L1440 90V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" fill="#FDFBF7"/>
        </svg>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div className="text-center">
      <span className="block text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: '#EAB308' }}>{number}</span>
      <span className="text-sm" style={{ color: 'rgba(255,255,255,0.75)' }}>{label}</span>
    </div>
  );
}

// ============================================
// CATEGORIES SECTION
// ============================================
function CategoriesSection() {
  const categories = [
    { name: 'Groceries', count: '80+', href: '/shop', image: 'https://images.unsplash.com/photo-1652860213441-6622f9fec77f?w=600&q=80', large: true },
    { name: 'Frozen', count: '20+', href: '/category/Frozen', image: 'https://images.unsplash.com/photo-1635360065676-f47200966893?w=400&q=80' },
    { name: 'Oils', count: '40+', href: '/category/Oils', image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400&q=80' },
    { name: 'Rice & Grains', count: '25+', href: '/category/Rice & Grains', image: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?w=400&q=80' },
  ];

  return (
    <section className="py-20 bg-[#FDFBF7]" data-testid="categories-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold mb-3" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="categories-title">
            Shop by Category
          </h2>
          <p className="text-gray-600 text-lg">Explore our wide range of authentic African products</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {categories.map((cat) => (
            <Link 
              key={cat.name}
              to={cat.href}
              className={`group relative min-h-[240px] rounded-3xl overflow-hidden ${cat.large ? 'lg:col-span-2 lg:row-span-2' : ''}`}
              data-testid={`category-${cat.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <img 
                src={cat.image} 
                alt={cat.name}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{cat.name}</h3>
                <span className="text-white/75 text-sm">{cat.count} Products</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// FEATURES SECTION
// ============================================
function FeaturesSection() {
  const features = [
    { icon: Leaf, title: 'Fresh Products', desc: 'Sourced directly from trusted suppliers for guaranteed freshness' },
    { icon: Truck, title: 'Fast Delivery', desc: 'UK-wide delivery within 24-48 hours to your doorstep' },
    { icon: Tags, title: 'Best Prices', desc: 'Competitive wholesale prices with great discounts' },
    { icon: Headphones, title: '24/7 Support', desc: 'Dedicated customer support via WhatsApp & chat' },
  ];

  return (
    <section className="py-16 bg-[#F8F5F0]" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div 
              key={f.title}
              className="text-center p-8 bg-white rounded-3xl border border-[#064E3B]/10 hover:-translate-y-1 hover:shadow-xl hover:border-transparent transition-all"
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#064E3B] to-[#065F46] rounded-2xl mb-5 text-white">
                <f.icon className="w-7 h-7" />
              </div>
              <h3 className="text-lg font-bold mb-2">{f.title}</h3>
              <p className="text-gray-500 text-[15px]">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ============================================
// PRODUCTS SECTION
// ============================================
function ProductsSection({ products, addToCart, currentPage, totalPages, setCurrentPage, toggleWishlist, isInWishlist }) {
  return (
    <section className="py-20 bg-[#FDFBF7]" data-testid="products-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-12">
          <div>
            <h2 className="text-3xl lg:text-4xl font-bold mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="products-title">
              Popular Products
            </h2>
            <p className="text-gray-600">Discover our most loved Nigerian food items</p>
          </div>
          <Link to="/shop" className="inline-flex items-center gap-2 text-[#064E3B] font-semibold hover:gap-3 transition-all" data-testid="view-all-link">
            View All <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="products-grid">
          {products.map(product => (
            <ProductCard 
              key={product.id} 
              product={product} 
              addToCart={addToCart} 
              toggleWishlist={toggleWishlist}
              isInWishlist={isInWishlist}
            />
          ))}
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-12" data-testid="pagination">
            {currentPage > 1 && (
              <button 
                onClick={() => setCurrentPage(p => p - 1)}
                className="w-11 h-11 flex items-center justify-center bg-white border border-[#064E3B]/10 rounded-xl hover:bg-[#064E3B] hover:text-white transition-all"
                data-testid="pagination-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            )}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let page = i + 1;
              if (totalPages > 5) {
                if (currentPage > 3) page = currentPage - 2 + i;
                if (currentPage > totalPages - 2) page = totalPages - 4 + i;
              }
              return (
                <button 
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-11 h-11 flex items-center justify-center rounded-xl font-semibold transition-all ${
                    page === currentPage 
                      ? 'bg-[#064E3B] text-white' 
                      : 'bg-white border border-[#064E3B]/10 hover:bg-[#064E3B] hover:text-white'
                  }`}
                  data-testid={`pagination-${page}`}
                >
                  {page}
                </button>
              );
            })}
            {currentPage < totalPages && (
              <button 
                onClick={() => setCurrentPage(p => p + 1)}
                className="w-11 h-11 flex items-center justify-center bg-white border border-[#064E3B]/10 rounded-xl hover:bg-[#064E3B] hover:text-white transition-all"
                data-testid="pagination-next"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function ProductCard({ product, addToCart, toggleWishlist, isInWishlist }) {
  const stars = product.star || 4;
  const inWishlist = isInWishlist ? isInWishlist(product.id) : false;
  const stockInfo = product.inventory || {};
  const inStock = product.inStock !== false && (stockInfo.stock === undefined || stockInfo.stock > 0);
  const lowStock = stockInfo.stock > 0 && stockInfo.stock <= 10;
  
  return (
    <Link 
      to={`/product/${product.id}`}
      className="group bg-white rounded-3xl overflow-hidden border border-[#064E3B]/10 hover:-translate-y-2 hover:shadow-xl hover:border-transparent transition-all duration-300 cursor-pointer block"
      data-testid={`product-${product.id}`}
    >
      <div className="relative aspect-square overflow-hidden bg-gray-100">
        <img 
          src={product.image} 
          alt={product.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          loading="lazy"
          onError={(e) => e.target.src = '/placeholder.jpg'}
        />
        {product.discountedPrice && product.discountedPrice > product.price && (
          <span className="absolute top-3 left-3 px-3 py-1 bg-[#C2410C] text-white text-xs font-bold rounded-full">
            SALE
          </span>
        )}
        {!inStock && (
          <span className="absolute top-3 left-3 px-3 py-1 bg-gray-800 text-white text-xs font-bold rounded-full">
            OUT OF STOCK
          </span>
        )}
        {lowStock && inStock && (
          <span className="absolute top-3 left-3 px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
            ONLY {stockInfo.stock} LEFT
          </span>
        )}
        {/* Wishlist Button */}
        {toggleWishlist && (
          <button
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggleWishlist(product); }}
            className={`absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 ${
              inWishlist 
                ? 'bg-[#C2410C] text-white' 
                : 'bg-white/90 text-gray-600 hover:bg-[#C2410C] hover:text-white'
            }`}
            data-testid={`wishlist-btn-${product.id}`}
          >
            <Heart className={`w-4 h-4 ${inWishlist ? 'fill-white' : ''}`} />
          </button>
        )}
        {/* Quick View Overlay */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 px-4 py-2 bg-white/95 backdrop-blur-sm text-gray-900 text-xs font-semibold rounded-full opacity-0 group-hover:opacity-100 transition-all">
          View Details
        </div>
      </div>
      <div className="p-4">
        <h3 className="text-[15px] font-semibold text-gray-900 mb-2 line-clamp-2 min-h-[40px] hover:text-[#064E3B] transition-colors">
          {product.name}
        </h3>
        <div className="flex items-center gap-0.5 mb-3">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`w-3.5 h-3.5 ${i < stars ? 'text-[#EAB308] fill-[#EAB308]' : 'text-gray-300'}`} 
            />
          ))}
          {product.review_count && (
            <span className="text-xs text-gray-400 ml-1">({product.review_count})</span>
          )}
        </div>
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xl font-bold text-[#064E3B]">£{product.price?.toFixed(2)}</span>
          {product.discountedPrice && (
            <span className="text-sm text-gray-400 line-through">£{product.discountedPrice?.toFixed(2)}</span>
          )}
        </div>
        <button 
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); addToCart(product); }}
          disabled={!inStock}
          className={`w-full py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all ${
            inStock 
              ? 'bg-[#064E3B] text-white hover:bg-[#065F46] hover:scale-[1.02]'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          }`}
          style={{ fontFamily: "'Manrope', sans-serif" }}
          data-testid={`add-to-cart-${product.id}`}
        >
          <ShoppingBag className="w-4 h-4" /> {inStock ? 'Add to Cart' : 'Out of Stock'}
        </button>
      </div>
    </Link>
  );
}

// ============================================
// PRODUCT DETAIL PAGE
// ============================================
function ProductDetailPage({ products, addToCart, toggleWishlist, isInWishlist, showNotification }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('description');
  const { token, isAuthenticated } = useAuth();

  // Fetch product data
  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        // Try API first
        const res = await fetch(`${API_URL}/api/products/${id}`);
        if (res.ok) {
          const data = await res.json();
          setProduct(data);
          
          // Fetch related products from same category
          if (data.category) {
            const related = products.filter(p => 
              p.category === data.category && p.id !== data.id
            ).slice(0, 4);
            setRelatedProducts(related);
          }
        } else {
          // Fallback to local products array
          const localProduct = products.find(p => p.id === parseInt(id) || p.id === id);
          if (localProduct) {
            setProduct(localProduct);
            const related = products.filter(p => 
              p.category === localProduct.category && p.id !== localProduct.id
            ).slice(0, 4);
            setRelatedProducts(related);
          }
        }
        
        // Fetch reviews
        const reviewsRes = await fetch(`${API_URL}/api/products/${id}/reviews`);
        if (reviewsRes.ok) {
          const reviewsData = await reviewsRes.json();
          setReviews(reviewsData.reviews || []);
        }
      } catch (error) {
        console.error('Error fetching product:', error);
        // Try local products
        const localProduct = products.find(p => p.id === parseInt(id) || p.id === id);
        if (localProduct) {
          setProduct(localProduct);
          const related = products.filter(p => 
            p.category === localProduct.category && p.id !== localProduct.id
          ).slice(0, 4);
          setRelatedProducts(related);
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (id) fetchProduct();
  }, [id, products]);

  // Track recently viewed
  useEffect(() => {
    if (product) {
      const recentKey = 'tisho_recently_viewed';
      const stored = JSON.parse(localStorage.getItem(recentKey) || '[]');
      const filtered = stored.filter(p => p.id !== product.id);
      const updated = [
        { id: product.id, name: product.name, price: product.price, image: product.image, viewedAt: new Date().toISOString() },
        ...filtered
      ].slice(0, 12);
      localStorage.setItem(recentKey, JSON.stringify(updated));
    }
  }, [product]);

  const handleAddToCart = () => {
    for (let i = 0; i < quantity; i++) {
      addToCart(product);
    }
    showNotification(`${quantity} x ${product.name} added to cart!`);
  };

  const handleShare = async (platform) => {
    const url = window.location.href;
    const text = `Check out ${product.name} at TishoFood!`;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
        break;
      case 'copy':
        await navigator.clipboard.writeText(url);
        showNotification('Link copied to clipboard!');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#064E3B] mx-auto mb-4" />
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Product Not Found</h2>
          <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
          <button 
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#064E3B] text-white rounded-xl font-semibold hover:bg-[#065F46] transition-colors"
          >
            Back to Shop
          </button>
        </div>
      </div>
    );
  }

  const inWishlist = isInWishlist ? isInWishlist(product.id) : false;
  const stars = product.star || 4;
  const stockInfo = product.inventory || {};
  const inStock = product.inStock !== false && (stockInfo.stock === undefined || stockInfo.stock > 0);
  const lowStock = stockInfo.stock > 0 && stockInfo.stock <= 10;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="product-detail-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 py-4">
          <nav className="flex items-center gap-2 text-sm">
            <button onClick={() => navigate('/')} className="text-gray-500 hover:text-[#064E3B]">Home</button>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">{product.category || 'Products'}</span>
            <ChevronRight className="w-4 h-4 text-gray-400" />
            <span className="text-[#064E3B] font-medium">{product.name}</span>
          </nav>
        </div>
      </div>

      {/* Main Product Section */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-8 lg:py-12">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="relative aspect-square bg-white rounded-3xl overflow-hidden border border-gray-100">
              <img 
                src={product.image} 
                alt={product.name}
                className="w-full h-full object-contain p-8"
                onError={(e) => e.target.src = '/placeholder.jpg'}
                loading="eager"
              />
              {product.discountedPrice && product.discountedPrice > product.price && (
                <span className="absolute top-4 left-4 px-4 py-2 bg-[#C2410C] text-white text-sm font-bold rounded-full">
                  SALE
                </span>
              )}
              {!inStock && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <span className="bg-white text-gray-900 px-6 py-3 rounded-full font-bold">Out of Stock</span>
                </div>
              )}
            </div>
            
            {/* Thumbnails placeholder for multiple images */}
            <div className="flex gap-3">
              {[product.image].map((img, idx) => (
                <button 
                  key={idx}
                  onClick={() => setSelectedImage(idx)}
                  className={`w-20 h-20 rounded-xl overflow-hidden border-2 transition-all ${
                    selectedImage === idx ? 'border-[#064E3B]' : 'border-gray-200'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            {/* Category & SKU */}
            <div className="flex items-center gap-3">
              <span className="px-3 py-1 bg-[#064E3B]/10 text-[#064E3B] text-xs font-semibold rounded-full">
                {product.category || 'General'}
              </span>
              {stockInfo.sku && (
                <span className="text-xs text-gray-400">SKU: {stockInfo.sku}</span>
              )}
            </div>

            {/* Title */}
            <h1 
              className="text-3xl lg:text-4xl font-bold text-gray-900"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {product.name}
            </h1>

            {/* Rating */}
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    className={`w-5 h-5 ${i < stars ? 'text-[#EAB308] fill-[#EAB308]' : 'text-gray-300'}`} 
                  />
                ))}
              </div>
              <span className="text-sm text-gray-600">
                {product.star || 4.0} ({reviews.length || product.review_count || 0} reviews)
              </span>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4">
              <span className="text-4xl font-bold text-[#064E3B]">£{product.price?.toFixed(2)}</span>
              {product.discountedPrice && product.discountedPrice > product.price && (
                <span className="text-xl text-gray-400 line-through">£{product.discountedPrice?.toFixed(2)}</span>
              )}
            </div>

            {/* Stock Status */}
            <div className="flex items-center gap-2">
              {inStock ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600 font-medium">
                    {lowStock ? `Only ${stockInfo.stock} left in stock!` : 'In Stock'}
                  </span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-red-600 font-medium">Out of Stock</span>
                </>
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 leading-relaxed">
              {product.description || `Authentic ${product.name} - Premium quality African food product. Perfect for traditional recipes and everyday cooking. Sourced from trusted suppliers to ensure the best taste and freshness.`}
            </p>

            {/* Quantity & Add to Cart */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <div className="flex items-center gap-6">
                <span className="text-sm font-semibold text-gray-700">Quantity:</span>
                <div className="flex items-center border border-gray-300 rounded-xl overflow-hidden">
                  <button 
                    onClick={() => setQuantity(Math.max(1, quantity - 1))}
                    className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 transition-colors"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="w-16 text-center font-semibold">{quantity}</span>
                  <button 
                    onClick={() => setQuantity(quantity + 1)}
                    className="w-12 h-12 flex items-center justify-center hover:bg-gray-100 transition-colors"
                    disabled={!inStock}
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex gap-4">
                <button 
                  onClick={handleAddToCart}
                  disabled={!inStock}
                  className="flex-1 py-4 bg-[#064E3B] text-white rounded-xl font-bold text-lg flex items-center justify-center gap-3 hover:bg-[#065F46] disabled:bg-gray-300 disabled:cursor-not-allowed transition-all"
                  data-testid="add-to-cart-detail"
                >
                  <ShoppingBag className="w-5 h-5" />
                  {inStock ? 'Add to Cart' : 'Out of Stock'}
                </button>
                
                <button 
                  onClick={() => toggleWishlist && toggleWishlist(product)}
                  className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center transition-all ${
                    inWishlist 
                      ? 'bg-[#C2410C] border-[#C2410C] text-white' 
                      : 'border-gray-300 text-gray-600 hover:border-[#C2410C] hover:text-[#C2410C]'
                  }`}
                  data-testid="wishlist-detail"
                >
                  <Heart className={`w-6 h-6 ${inWishlist ? 'fill-white' : ''}`} />
                </button>
              </div>
            </div>

            {/* Delivery Info */}
            <div className="grid grid-cols-2 gap-4 pt-6">
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                <Truck className="w-6 h-6 text-[#064E3B]" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Free Delivery</p>
                  <p className="text-xs text-gray-500">Over £150 in SR, DH and NE only</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                <Clock className="w-6 h-6 text-[#064E3B]" />
                <div>
                  <p className="text-sm font-semibold text-gray-900">Next Day</p>
                  <p className="text-xs text-gray-500">Order before 2pm</p>
                </div>
              </div>
            </div>

            {/* Share */}
            <div className="flex items-center gap-4 pt-4">
              <span className="text-sm font-semibold text-gray-700">Share:</span>
              <div className="flex gap-2">
                <button 
                  onClick={() => handleShare('facebook')}
                  className="w-10 h-10 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
                >
                  <Facebook className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleShare('twitter')}
                  className="w-10 h-10 rounded-full bg-[#1DA1F2] text-white flex items-center justify-center hover:opacity-80 transition-opacity"
                >
                  <Twitter className="w-5 h-5" />
                </button>
                <button 
                  onClick={() => handleShare('copy')}
                  className="w-10 h-10 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center hover:bg-gray-300 transition-colors"
                >
                  <Copy className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Section */}
        <div className="mt-16">
          <div className="flex border-b border-gray-200">
            {['description', 'reviews', 'shipping'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-6 py-4 font-semibold capitalize transition-colors ${
                  activeTab === tab 
                    ? 'text-[#064E3B] border-b-2 border-[#064E3B]' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'reviews' ? `Reviews (${reviews.length || 0})` : tab}
              </button>
            ))}
          </div>
          
          <div className="py-8">
            {activeTab === 'description' && (
              <div className="prose max-w-none">
                <p className="text-gray-600 leading-relaxed">
                  {product.description || `${product.name} is a premium quality African food product available at TishoFood. 
                  
                  This authentic product is perfect for traditional Nigerian and African recipes. We source our products directly from trusted suppliers to ensure you get the best quality and freshest items.
                  
                  Whether you're cooking a family meal or preparing for a special occasion, this product will add authentic flavor to your dishes.`}
                </p>
                {product.keywords && (
                  <div className="mt-6">
                    <h4 className="font-semibold text-gray-900 mb-2">Related Keywords:</h4>
                    <div className="flex flex-wrap gap-2">
                      {product.keywords.map((keyword, idx) => (
                        <span key={idx} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">
                          {keyword}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'reviews' && (
              <div className="space-y-6">
                {reviews.length > 0 ? (
                  reviews.map((review, idx) => (
                    <div key={idx} className="p-6 bg-white rounded-xl border border-gray-100">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 bg-[#064E3B] text-white rounded-full flex items-center justify-center font-bold">
                          {review.user_name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{review.user_name || 'Anonymous'}</p>
                          <div className="flex items-center gap-1">
                            {[...Array(5)].map((_, i) => (
                              <Star key={i} className={`w-3 h-3 ${i < review.rating ? 'text-[#EAB308] fill-[#EAB308]' : 'text-gray-300'}`} />
                            ))}
                          </div>
                        </div>
                      </div>
                      {review.title && <h4 className="font-semibold text-gray-900 mb-2">{review.title}</h4>}
                      <p className="text-gray-600">{review.comment}</p>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12">
                    <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No reviews yet. Be the first to review this product!</p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'shipping' && (
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">Delivery Information</h4>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#064E3B] mt-0.5" />
                      <span>Free delivery over £150 in SR, DH and NE only</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#064E3B] mt-0.5" />
                      <span>Next day delivery available (order before 2pm)</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#064E3B] mt-0.5" />
                      <span>UK-wide delivery coverage</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#064E3B] mt-0.5" />
                      <span>Careful packaging to ensure freshness</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-4">Returns Policy</h4>
                  <ul className="space-y-3 text-gray-600">
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#064E3B] mt-0.5" />
                      <span>14-day return policy for non-perishables</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#064E3B] mt-0.5" />
                      <span>Full refund for damaged items</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#064E3B] mt-0.5" />
                      <span>Contact us within 48 hours of delivery</span>
                    </li>
                  </ul>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-16">
            <h2 
              className="text-2xl font-bold text-gray-900 mb-8"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              You May Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {relatedProducts.map(relProduct => (
                <Link 
                  key={relProduct.id}
                  to={`/product/${relProduct.id}`}
                  className="bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all cursor-pointer group block"
                  data-testid={`related-product-${relProduct.id}`}
                >
                  <div className="aspect-square bg-gray-50 overflow-hidden">
                    <img 
                      src={relProduct.image} 
                      alt={relProduct.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 text-sm truncate mb-2">{relProduct.name}</h3>
                    <p className="text-[#064E3B] font-bold">£{relProduct.price?.toFixed(2)}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// PROMO SECTION
// ============================================
function PromoSection({ showNotification }) {
  const [email, setEmail] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      showNotification('Thank you for subscribing!');
      setEmail('');
    }
  };

  return (
    <section className="py-20 bg-[#F8F5F0]" data-testid="promo-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="bg-gradient-to-br from-[#064E3B] to-[#053D2E] rounded-3xl overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-8 p-8 lg:p-16">
            <div className="text-white">
              <span className="inline-block px-4 py-1.5 bg-[#C2410C] rounded-full text-xs font-bold uppercase mb-5">Limited Offer</span>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Get 10% Off Your First Order
              </h2>
              <p className="text-white/85 mb-8 max-w-md">
                Sign up for our newsletter and receive exclusive discounts and updates on new products.
              </p>
              <form onSubmit={handleSubmit} className="flex flex-wrap gap-3" data-testid="newsletter-form">
                <input 
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="flex-1 min-w-[200px] px-5 py-3.5 bg-white/15 border border-white/30 rounded-full text-white placeholder-white/60 focus:outline-none focus:border-white backdrop-blur-sm"
                  data-testid="newsletter-input"
                />
                <button 
                  type="submit"
                  className="px-8 py-3.5 bg-[#C2410C] text-white rounded-full font-semibold hover:bg-[#EA580C] transition-all"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                  data-testid="newsletter-btn"
                >
                  Subscribe
                </button>
              </form>
            </div>
            <div className="hidden lg:flex items-center justify-center">
              <img 
                src="https://images.unsplash.com/photo-1702827482556-481adcd68f3b?w=500&q=80"
                alt="Fresh African Ingredients"
                className="max-w-full rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ============================================
// CART SIDEBAR (Updated with auth check)
// ============================================
function CartSidebar({ isOpen, cart, cartTotal, updateQuantity, removeFromCart, onClose, onCheckout, isAuthenticated, onShowAuth }) {
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[1050]" onClick={onClose} data-testid="cart-overlay" />
      <aside className="fixed top-0 right-0 w-full max-w-md h-full bg-white shadow-xl z-[1100] flex flex-col" data-testid="cart-sidebar">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="flex items-center gap-2 text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            <ShoppingBag className="w-5 h-5 text-[#064E3B]" /> Your Cart
          </h3>
          <button onClick={onClose} className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center hover:bg-[#C2410C] hover:text-white transition-all" data-testid="cart-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="flex-1 overflow-y-auto p-5" data-testid="cart-items">
          {cart.length === 0 ? (
            <div className="text-center py-12 text-gray-400" data-testid="cart-empty">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Your cart is empty</p>
            </div>
          ) : (
            cart.map(item => (
              <div key={item.id} className="flex gap-4 py-4 border-b border-gray-100" data-testid={`cart-item-${item.id}`}>
                <img 
                  src={item.image} 
                  alt={item.name}
                  className="w-20 h-20 object-cover rounded-xl bg-gray-100"
                  onError={(e) => e.target.src = '/placeholder.jpg'}
                  loading="lazy"
                />
                <div className="flex-1">
                  <p className="font-semibold text-gray-900 mb-1">{item.name}</p>
                  <p className="text-[#064E3B] font-semibold">£{(item.price * item.quantity).toFixed(2)}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <button 
                      onClick={() => updateQuantity(item.id, -1)}
                      className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-[#064E3B] hover:text-white transition-all"
                      data-testid={`qty-minus-${item.id}`}
                    >
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <button 
                      onClick={() => updateQuantity(item.id, 1)}
                      className="w-7 h-7 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-[#064E3B] hover:text-white transition-all"
                      data-testid={`qty-plus-${item.id}`}
                    >
                      <Plus className="w-3 h-3" />
                    </button>
                    <button 
                      onClick={() => removeFromCart(item.id)}
                      className="ml-auto w-7 h-7 text-gray-400 hover:text-[#C2410C] transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="p-5 border-t border-gray-100 bg-gray-50">
          <div className="text-xl font-bold text-center mb-4" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="cart-total">
            Total: £{cartTotal.toFixed(2)}
          </div>
          
          {!isAuthenticated && cart.length > 0 && (
            <p className="text-sm text-center text-amber-600 mb-3 flex items-center justify-center gap-1">
              <Lock className="w-4 h-4" />
              Please sign in to checkout
            </p>
          )}
          
          <button 
            onClick={isAuthenticated ? onCheckout : onShowAuth}
            className="w-full py-4 bg-[#064E3B] text-white rounded-xl font-semibold flex items-center justify-center gap-2 hover:bg-[#065F46] transition-all disabled:opacity-50"
            style={{ fontFamily: "'Manrope', sans-serif" }}
            disabled={cart.length === 0}
            data-testid="checkout-btn"
          >
            {isAuthenticated ? (
              <>
                <Lock className="w-4 h-4" /> Proceed to Checkout
              </>
            ) : (
              <>
                <User className="w-4 h-4" /> Sign In to Checkout
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
}

// ============================================
// FOOTER
// ============================================
function Footer() {
  const quickLinks = [
    { name: 'Shop All', href: '/shop' },
    { name: 'About Us', href: '/about' },
    { name: 'Contact', href: '/contact' },
    { name: 'FAQs', href: '/faqs' }
  ];

  const categories = [
    { name: 'Groceries', href: '/shop' },
    { name: 'Frozen', href: '/category/Frozen' },
    { name: 'Oils', href: '/category/Oils' },
    { name: 'Rice & Grains', href: '/category/Rice & Grains' }
  ];

  const customerService = [
    { name: 'Delivery Info', href: '/delivery' },
    { name: 'Returns Policy', href: '/returns' },
    { name: 'Privacy Policy', href: '/privacy' },
    { name: 'Terms & Conditions', href: '/terms' }
  ];

  const socialLinks = [
    { icon: 'facebook-f', href: 'https://facebook.com/tishofoods' },
    { icon: 'instagram', href: 'https://instagram.com/tishofoods' },
    { icon: 'tiktok', href: 'https://tiktok.com/@tishofoods' },
    { icon: 'whatsapp', href: 'https://wa.me/447519935205' }
  ];

  return (
    <footer className="bg-[#0F172A] text-white pt-16" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-12 border-b border-white/10">
          <div className="max-w-xs">
            <a href="/" className="flex items-center gap-3 text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              <img src="/Tishologo.png" alt="Tisho Food" className="w-12 h-12 rounded-xl" />
              TishoFood
            </a>
            <p className="text-white/70 text-[15px] mb-6">
              Bringing authentic Nigerian and African foods to your doorstep across the UK. Quality products, competitive prices, fast delivery.
            </p>
            <div className="flex gap-3">
              {socialLinks.map(social => (
                <a 
                  key={social.icon}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-11 h-11 bg-white/10 rounded-xl flex items-center justify-center hover:bg-[#064E3B] hover:-translate-y-0.5 transition-all"
                  data-testid={`social-${social.icon}`}
                >
                  <i className={`fab fa-${social.icon}`}></i>
                </a>
              ))}
            </div>
          </div>
          
          <div>
            <h4 className="font-bold mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map(link => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-white/70 hover:text-[#EAB308] transition-colors text-[15px]"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-5">Categories</h4>
            <ul className="space-y-3">
              {categories.map(link => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-white/70 hover:text-[#EAB308] transition-colors text-[15px]"
                    data-testid={`footer-category-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-5">Customer Service</h4>
            <ul className="space-y-3">
              {customerService.map(link => (
                <li key={link.name}>
                  <Link 
                    to={link.href} 
                    className="text-white/70 hover:text-[#EAB308] transition-colors text-[15px]"
                    data-testid={`footer-service-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="py-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/60 text-sm">&copy; {new Date().getFullYear()} TishoFood. All rights reserved.</p>
          <div className="flex gap-4 text-2xl text-white/50">
            <i className="fab fa-cc-visa"></i>
            <i className="fab fa-cc-mastercard"></i>
            <i className="fab fa-cc-amex"></i>
            <i className="fab fa-cc-paypal"></i>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// FLOATING BUTTONS
// ============================================
function FloatingButtons({ setIsChatOpen }) {
  return (
    <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-50">
      <a 
        href="https://wa.me/4407519935205"
        target="_blank"
        rel="noopener noreferrer"
        className="w-14 h-14 bg-[#25D366] text-white rounded-full flex items-center justify-center text-2xl shadow-lg hover:scale-110 hover:bg-[#128C7E] transition-all"
        data-testid="whatsapp-btn"
      >
        <i className="fab fa-whatsapp"></i>
      </a>
      <button 
        onClick={() => setIsChatOpen(true)}
        className="w-14 h-14 bg-[#064E3B] text-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 hover:bg-[#065F46] transition-all"
        data-testid="chat-toggle"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    </div>
  );
}

// ============================================
// CHAT WIDGET
// ============================================
function ChatWidget({ isOpen, onClose }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([
    { from: 'bot', text: "Hello! 👋 I'm your TishoFood assistant. How can I help you today?\n\nAsk me about:\n• 🚚 Delivery\n• 🛒 Products\n• ⏰ Business hours\n• 💳 Payments\n• ↩️ Returns" }
  ]);
  const [isTyping, setIsTyping] = useState(false);

  const sendMessage = async () => {
    if (!message.trim()) return;
    
    const userMessage = message.trim();
    setMessages(prev => [...prev, { from: 'user', text: userMessage }]);
    setMessage('');
    setIsTyping(true);

    try {
      const response = await fetch(`${API_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      });
      
      if (response.ok) {
        const data = await response.json();
        setMessages(prev => [...prev, { from: 'bot', text: data.response }]);
      } else {
        setMessages(prev => [...prev, { 
          from: 'bot', 
          text: "Sorry, I'm having trouble connecting. Please try again or contact us at +44 7519 935205." 
        }]);
      }
    } catch (error) {
      setMessages(prev => [...prev, { 
        from: 'bot', 
        text: "I'm temporarily unavailable. Please contact us directly at info@tishofood.co.uk or +44 7519 935205." 
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const quickQuestions = [
    { label: "🚚 Delivery", question: "What are your delivery options?" },
    { label: "⏰ Hours", question: "What are your business hours?" },
    { label: "💳 Payment", question: "What payment methods do you accept?" }
  ];

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-28 right-6 w-[380px] max-w-[calc(100vw-3rem)] bg-white rounded-3xl shadow-2xl z-[950] overflow-hidden" style={{ animation: 'slideUp 0.3s ease' }} data-testid="chatbot-widget">
      <div className="flex items-center gap-3 p-4 bg-[#064E3B] text-white">
        <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">🍲</div>
        <div className="flex-1">
          <h4 className="font-semibold text-[15px]">TishoFood Assistant</h4>
          <span className="text-xs text-white/80 flex items-center gap-1">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span> Online
          </span>
        </div>
        <button 
          onClick={onClose} 
          className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center hover:bg-white/30 transition-all cursor-pointer" 
          data-testid="chatbot-close"
          type="button"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="h-80 overflow-y-auto p-4 space-y-3 bg-gray-50">
        {messages.map((msg, i) => (
          <div 
            key={i}
            className={`max-w-[90%] p-3 rounded-2xl text-[14px] leading-relaxed whitespace-pre-wrap ${
              msg.from === 'bot' 
                ? 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100' 
                : 'bg-[#064E3B] text-white ml-auto rounded-br-sm'
            }`}
          >
            {msg.text}
          </div>
        ))}
        {isTyping && (
          <div className="bg-white text-gray-500 p-3 rounded-2xl rounded-bl-sm w-20 shadow-sm border border-gray-100">
            <div className="flex gap-1">
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
              <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
            </div>
          </div>
        )}
      </div>

      {/* Quick Questions */}
      <div className="flex gap-2 px-4 py-2 border-t border-gray-100 bg-white overflow-x-auto">
        {quickQuestions.map((q, i) => (
          <button
            key={i}
            onClick={() => { setMessage(q.question); }}
            className="flex-shrink-0 px-3 py-1.5 bg-gray-100 hover:bg-[#064E3B] hover:text-white rounded-full text-xs font-medium transition-all"
          >
            {q.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2 p-4 border-t border-gray-100 bg-white">
        <input 
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && !isTyping && sendMessage()}
          placeholder="Type your message..."
          className="flex-1 px-4 py-2.5 border border-gray-200 rounded-full text-[15px] focus:outline-none focus:border-[#064E3B] focus:ring-2 focus:ring-[#064E3B]/10"
          data-testid="chat-input"
          disabled={isTyping}
        />
        <button 
          onClick={sendMessage}
          disabled={isTyping || !message.trim()}
          className="w-11 h-11 bg-[#064E3B] text-white rounded-full flex items-center justify-center hover:bg-[#065F46] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="send-btn"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>
    </div>
  );
}

export default App;

// ============================================
// P1 - PROFILE PAGE
// ============================================
function ProfilePage({ user, token, onBack, showNotification, onLogout }) {
  const { updateProfile } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Load orders
    fetch(`${API_URL}/api/orders`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(res => res.ok ? res.json() : [])
      .then(data => setOrders(data))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateProfile({ full_name: fullName, phone });
      showNotification('Profile updated successfully!');
      setEditing(false);
    } catch (err) {
      showNotification(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-4 lg:px-8">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#064E3B] mb-8" data-testid="profile-back-btn">
          <ArrowLeft className="w-5 h-5" /> Back to Shop
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Info */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-[#064E3B] to-[#065F46] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-3xl font-bold text-white">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </span>
                </div>
                <h2 className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {user?.full_name}
                </h2>
                <p className="text-gray-500 text-sm">{user?.email}</p>
                {user?.email_verified && (
                  <span className="inline-flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full mt-2">
                    <Check className="w-3 h-3" /> Verified
                  </span>
                )}
              </div>

              {editing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B]"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone (Optional)</label>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:border-[#064E3B]"
                      placeholder="+44 7XXX XXXXXX"
                      data-testid="profile-phone-input"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 py-2 bg-[#064E3B] text-white rounded-xl font-semibold hover:bg-[#065F46] disabled:opacity-50"
                      data-testid="profile-save-btn"
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      onClick={() => { setEditing(false); setFullName(user?.full_name || ''); setPhone(user?.phone || ''); }}
                      className="px-4 py-2 border border-gray-200 rounded-xl hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                    <Mail className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Email</p>
                      <p className="text-sm font-medium">{user?.email}</p>
                    </div>
                  </div>
                  {user?.phone && (
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <Phone className="w-5 h-5 text-gray-400" />
                      <div>
                        <p className="text-xs text-gray-500">Phone</p>
                        <p className="text-sm font-medium">{user?.phone}</p>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setEditing(true)}
                    className="w-full py-2 border border-[#064E3B] text-[#064E3B] rounded-xl font-semibold hover:bg-[#064E3B]/5 flex items-center justify-center gap-2"
                    data-testid="profile-edit-btn"
                  >
                    <Edit2 className="w-4 h-4" /> Edit Profile
                  </button>
                </div>
              )}

              <button
                onClick={onLogout}
                className="w-full mt-4 py-2 border border-red-200 text-red-600 rounded-xl font-semibold hover:bg-red-50 flex items-center justify-center gap-2"
                data-testid="profile-logout-btn"
              >
                <LogOut className="w-4 h-4" /> Sign Out
              </button>
            </div>
          </div>

          {/* Order History */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-3xl p-6 border border-gray-100">
              <h3 className="text-xl font-bold mb-6 flex items-center gap-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                <Package className="w-6 h-6 text-[#064E3B]" /> Order History
              </h3>

              {loading ? (
                <div className="text-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-[#064E3B] mx-auto" />
                </div>
              ) : orders.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>No orders yet</p>
                  <p className="text-sm">Your order history will appear here</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders.map(order => (
                    <div key={order.order_id} className="border border-gray-100 rounded-2xl p-4" data-testid={`order-${order.order_id}`}>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="font-mono text-sm text-gray-500">#{order.order_id?.slice(0, 8)}</p>
                          <p className="text-xs text-gray-400">{new Date(order.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          order.status === 'completed' ? 'bg-green-100 text-green-700' :
                          order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
                        </span>
                      </div>
                      <div className="flex gap-2 mb-3">
                        {order.items?.slice(0, 3).map((item, i) => (
                          <img 
                            key={i}
                            src={item.image} 
                            alt={item.name}
                            className="w-12 h-12 object-cover rounded-lg bg-gray-100"
                            onError={(e) => e.target.src = '/placeholder.jpg'}
                            loading="lazy"
                          />
                        ))}
                        {order.items?.length > 3 && (
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-sm text-gray-500">
                            +{order.items.length - 3}
                          </div>
                        )}
                      </div>
                      <div className="flex justify-between items-center">
                        <p className="text-sm text-gray-500">{order.items?.length} item(s)</p>
                        <p className="font-bold text-[#064E3B]">£{order.total?.toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================
// P2 - WISHLIST PAGE
// ============================================
function WishlistPage({ wishlist, onBack, addToCart, toggleWishlist }) {
  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="wishlist-page">
      <div className="max-w-6xl mx-auto px-4 lg:px-8">
        <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#064E3B] mb-8" data-testid="wishlist-back-btn">
          <ArrowLeft className="w-5 h-5" /> Back to Shop
        </button>

        <div className="flex items-center gap-3 mb-8">
          <Heart className="w-8 h-8 text-[#C2410C] fill-[#C2410C]" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
            My Wishlist
          </h1>
          <span className="bg-[#064E3B] text-white px-3 py-1 rounded-full text-sm">
            {wishlist.length} items
          </span>
        </div>

        {wishlist.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-3xl border border-gray-100">
            <Heart className="w-20 h-20 mx-auto mb-4 text-gray-300" />
            <h3 className="text-xl font-bold mb-2">Your wishlist is empty</h3>
            <p className="text-gray-500 mb-6">Save items you love by clicking the heart icon</p>
            <button onClick={onBack} className="px-8 py-3 bg-[#064E3B] text-white rounded-full font-semibold hover:bg-[#065F46]">
              Start Shopping
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
            {wishlist.map(item => (
              <div key={item.product_id} className="group bg-white rounded-3xl overflow-hidden border border-gray-100 hover:-translate-y-2 hover:shadow-xl transition-all" data-testid={`wishlist-item-${item.product_id}`}>
                <div className="relative aspect-square overflow-hidden bg-gray-100">
                  <img 
                    src={item.image} 
                    alt={item.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => e.target.src = '/placeholder.jpg'}
                    loading="lazy"
                  />
                  <button
                    onClick={() => toggleWishlist({ id: item.product_id, ...item })}
                    className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-lg hover:scale-110 transition-all"
                    data-testid={`remove-wishlist-${item.product_id}`}
                  >
                    <Heart className="w-5 h-5 text-[#C2410C] fill-[#C2410C]" />
                  </button>
                </div>
                <div className="p-4">
                  <h3 className="text-[15px] font-semibold text-gray-900 mb-2 line-clamp-2">{item.name}</h3>
                  <p className="text-xl font-bold text-[#064E3B] mb-4">£{item.price?.toFixed(2)}</p>
                  <button 
                    onClick={() => addToCart({ id: item.product_id, ...item })}
                    className="w-full py-3 bg-[#064E3B] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 hover:bg-[#065F46]"
                    data-testid={`add-cart-wishlist-${item.product_id}`}
                  >
                    <ShoppingBag className="w-4 h-4" /> Add to Cart
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// P2 - PRODUCT FILTERS
// ============================================
function ProductFilters({ filters, setFilters, showFilters, setShowFilters, totalProducts }) {
  const categories = ['Groceries', 'Meat', 'Oil', 'Dried Foods', 'Beverages', 'Snacks'];
  const sortOptions = [
    { value: '', label: 'Default' },
    { value: 'price_asc', label: 'Price: Low to High' },
    { value: 'price_desc', label: 'Price: High to Low' },
    { value: 'rating', label: 'Top Rated' }
  ];

  const clearFilters = () => {
    setFilters({ category: '', minPrice: '', maxPrice: '', minRating: '', sortBy: '' });
  };

  const hasActiveFilters = filters.category || filters.minPrice || filters.maxPrice || filters.minRating || filters.sortBy;

  return (
    <section className="py-4 bg-[#FDFBF7]" data-testid="product-filters-section">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-all ${
                showFilters ? 'bg-[#064E3B] text-white' : 'bg-white border border-gray-200 text-gray-700 hover:border-[#064E3B]'
              }`}
              data-testid="toggle-filters-btn"
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filters
              {hasActiveFilters && (
                <span className="w-2 h-2 bg-[#C2410C] rounded-full" />
              )}
            </button>
            <span className="text-sm text-gray-500">{totalProducts} products</span>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 hidden sm:block">Sort by:</span>
            <select
              value={filters.sortBy}
              onChange={(e) => setFilters(prev => ({ ...prev, sortBy: e.target.value }))}
              className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#064E3B]"
              data-testid="sort-select"
            >
              {sortOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>

        {showFilters && (
          <div className="bg-white rounded-2xl p-6 border border-gray-100 mb-4" data-testid="filters-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {/* Category Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#064E3B]"
                  data-testid="category-filter"
                >
                  <option value="">All Categories</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              {/* Price Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Price (£)</label>
                <input
                  type="number"
                  value={filters.minPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, minPrice: e.target.value }))}
                  placeholder="0"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#064E3B]"
                  data-testid="min-price-filter"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Max Price (£)</label>
                <input
                  type="number"
                  value={filters.maxPrice}
                  onChange={(e) => setFilters(prev => ({ ...prev, maxPrice: e.target.value }))}
                  placeholder="100"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#064E3B]"
                  data-testid="max-price-filter"
                />
              </div>

              {/* Rating Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Min Rating</label>
                <select
                  value={filters.minRating}
                  onChange={(e) => setFilters(prev => ({ ...prev, minRating: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#064E3B]"
                  data-testid="rating-filter"
                >
                  <option value="">Any Rating</option>
                  <option value="4">4+ Stars</option>
                  <option value="3">3+ Stars</option>
                  <option value="2">2+ Stars</option>
                </select>
              </div>
            </div>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 text-sm text-[#C2410C] hover:underline flex items-center gap-1"
                data-testid="clear-filters-btn"
              >
                <X className="w-4 h-4" /> Clear all filters
              </button>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
